/* Offline-safe queue for `/api/progress` saves.
 *
 * When the browser is offline (or a save fails with a network-level error),
 * the score is persisted to localStorage instead of being dropped. On the next
 * `online` event (or on app mount, or on the next successful backend health
 * check), a background flusher POSTs each queued item and removes it on
 * success.
 *
 * Duplicates are prevented server-side: each queued item carries a
 * client-generated `client_id` (UUID) that the backend uses as an idempotency
 * key. If we retry a POST that already went through, the backend returns the
 * existing record instead of creating a second score.
 *
 * The queue is per-browser (localStorage). A learner switching devices does
 * not carry their pending saves across — this is intentional: cross-device
 * sync is what the server is for.
 */

const KEY = "progress_queue_v1";

/* Generates a reasonably-unique client id. Prefer crypto.randomUUID when
   available (Chrome 92+, Safari 15.4+, Firefox 95+ — universal for our
   audience of modern browsers). Falls back to a timestamp+random string. */
export function newClientId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  return `cid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function readQueue() {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(KEY) : null;
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Corrupted localStorage entry — drop it rather than crash the app.
    return [];
  }
}

function writeQueue(items) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // Quota exceeded or private-mode denial — best-effort, silently ignore.
  }
}

/* Public API */

export function getQueue() { return readQueue(); }

export function getQueueSize() { return readQueue().length; }

export function clearQueue() { writeQueue([]); }

/* Adds a payload to the queue. Assigns a client_id if the caller didn't
   provide one, and stamps queuedAt for diagnostics/ordering. Returns the
   stored envelope (payload + queuedAt). */
export function queueProgress(payload) {
  const enriched = {
    ...payload,
    client_id: payload.client_id || newClientId(),
  };
  const envelope = { payload: enriched, queuedAt: new Date().toISOString() };
  const q = readQueue();
  q.push(envelope);
  writeQueue(q);
  return envelope;
}

/* Attempts to POST every queued item using the supplied `poster` function.
 * `poster(payload)` MUST return a promise that resolves on 2xx and rejects
 * with an axios-shaped error on failure. We treat the rejection differently
 * depending on `err.response?.status`:
 *
 *   - No response (network error / offline) → keep the item, stop flushing
 *   - 4xx / 5xx with a response                → drop the item + report the
 *                                                  status back to the caller
 *                                                  so it can show a toast.
 *     Rationale: a 402/403/422 will keep failing forever. Keeping it in the
 *     queue would silently retry every reconnect and never succeed. Better
 *     to surface the failure once and move on.
 *
 * Returns { sent, dropped, remaining, errors } where errors is a list of
 * { simulator_name, status, detail } for each dropped-with-error item. */
export async function flushQueue(poster) {
  const items = readQueue();
  if (!items.length) return { sent: 0, dropped: 0, remaining: 0, errors: [] };

  const survivors = [];
  const errors = [];
  let sent = 0;
  let dropped = 0;
  let stoppedByNetwork = false;

  for (const item of items) {
    if (stoppedByNetwork) {
      survivors.push(item);
      continue;
    }
    try {
      await poster(item.payload);
      sent += 1;
    } catch (err) {
      const status = err?.response?.status;
      if (status == null) {
        // Network / offline again — put back and stop trying.
        survivors.push(item);
        stoppedByNetwork = true;
      } else {
        dropped += 1;
        errors.push({
          simulator_name: item.payload?.simulator_name,
          status,
          detail: err?.response?.data?.detail,
        });
      }
    }
  }

  writeQueue(survivors);
  return { sent, dropped, remaining: survivors.length, errors };
}
